import { useTranslation } from "react-i18next";
import { Divider, Form, Icon, Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { isBrandGroupNameTaken } from "modules/BrandsDashboard/components/BrandGroupModal";
import { BrandSettingsFields } from "modules/BrandsDashboard/components/BrandSettingsFields";
import { useBrandGroupsQuery } from "modules/BrandsDashboard/hooks";

import type { BrandGroupFormPayload } from "modules/BrandsDashboard/components/BrandGroupModal/hooks";
import type { BrandSettingsFormValues } from "modules/BrandsDashboard/components/BrandSettingsFields";

export type BrandGroupFormValues = BrandGroupFormPayload & BrandSettingsFormValues;

type BrandGroupFormProps = {
  isEdit: boolean;
  isPending: boolean;
  onClose: () => void;
};

const FormHelper = createFormHelper<BrandGroupFormValues>();

export const BrandGroupForm = ({ isEdit, isPending, onClose }: BrandGroupFormProps) => {
  const { t } = useTranslation();
  const { watch, formState } = FormHelper.useFormContext();

  const { data: brandGroups = [] } = useBrandGroupsQuery();

  const [id, name] = watch(["id", "name"]);
  const isGroupNameTaken = isBrandGroupNameTaken(brandGroups, { id, name });

  return (
    <>
      <Modal.Header className="padding-container">
        <div className="header-container ">
          <div className="header-title">{isEdit ? t("v8_edit_brand_group", "Edit brand group") : t("v8_add_brand_group", "Add brand group")}</div>
          <FormHelper.Field.Checkbox name="is_active" label={t("v8_active", "Active")} />
        </div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container" scrolling>
        <Form.Field required error={formState.isSubmitted && (!name.trim() || isGroupNameTaken)}>
          <label>{t("v8_group", "Group")}</label>
          <FormHelper.Field.Input name="name" inputProps={{ placeholder: t("v8_group", "Group") }} />
          {formState.isSubmitted && isGroupNameTaken && <div className="field-error">{t("v8_group_already_exists", "Group already exists")}</div>}
        </Form.Field>

        <BrandSettingsFields />
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="action-buttons-container">
          <div className="right-buttons">
            <Form.Button type="button" className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            <Form.Button type="submit" color="green" disabled={isPending} loading={isPending}>
              {t("v8_save", "Save")}
              <Icon className="check" />
            </Form.Button>
          </div>
        </div>
      </Modal.Actions>
    </>
  );
};
